import { Badge, useMantineColorScheme } from "@mantine/core";
import type { ReactNode } from "react";
import type { AbsenceStatus, MonthlyWeekStatus } from "../api/client";
import type { Scheme, SlotColors, StatusSlot } from "../theme";
import { STATUS } from "../theme/tokens";

/** Все возможные статусы недельного таймшита, включая синтетические (`absence_only`, `no_week`). */
export type TimesheetStatus = MonthlyWeekStatus;

const TIMESHEET_TO_SLOT: Record<TimesheetStatus, StatusSlot> = {
  draft: "draft",
  submitted_to_line_manager: "submitted",
  rework_requested_by_line_manager: "rework",
  waiting_project_managers: "waiting",
  rework_requested_by_project_manager: "rework",
  approved: "approved",
  rolled_back_by_admin: "rolled",
  absence_only: "absence",
  no_week: "draft",
  timesheet_exempt: "draft",
};

const ABSENCE_TO_SLOT: Record<AbsenceStatus, StatusSlot> = {
  draft: "draft",
  // US-107: ступень РП — такое же ожидание решения, как и у ЛР.
  submitted_to_pm: "submitted",
  submitted: "submitted",
  approved_by_lr: "waiting",
  approved: "approved",
  returned: "rework",
};

/** Русскоязычные fallback-лейблы для случаев, когда у объекта нет `status_display`. */
const TIMESHEET_LABEL: Record<TimesheetStatus, string> = {
  draft: "Черновик",
  submitted_to_line_manager: "На ЛР",
  rework_requested_by_line_manager: "Возврат ЛР",
  waiting_project_managers: "На PM",
  rework_requested_by_project_manager: "Возврат PM",
  approved: "Согласован",
  rolled_back_by_admin: "Откат админа",
  absence_only: "Покрыт отсутствием",
  no_week: "Не создан",
  timesheet_exempt: "Не ведёт таймшит",
};

const ABSENCE_LABEL: Record<AbsenceStatus, string> = {
  draft: "Черновик",
  submitted_to_pm: "На согласовании у РП",
  submitted: "На согласовании",
  approved_by_lr: "Согласовано ЛР",
  approved: "Согласовано",
  returned: "Возвращено",
};

/** Symbols are intentionally Unicode — no icon dependency in this PR. */
const SLOT_ICON: Partial<Record<StatusSlot, string>> = {
  approved: "✓", // ✓
  rejected: "✕", // ✕
  rework: "↻", // ↻
  waiting: "⏱", // ⏱
};

export type StatusBadgeSize = "xs" | "sm" | "md";
export type StatusBadgeVariant = "soft" | "outline" | "dot";

interface CommonProps {
  size?: StatusBadgeSize;
  variant?: StatusBadgeVariant;
  withIcon?: boolean;
  /** Override the resolved label. Useful for `status_display` straight from API. */
  label?: string;
}

export type StatusBadgeProps = CommonProps &
  (
    | { entity: "timesheet"; code: TimesheetStatus }
    | { entity: "absence"; code: AbsenceStatus }
  );

function resolveSlot(props: StatusBadgeProps): StatusSlot {
  // Fall back to "draft" for codes the backend may add later — UI keeps rendering,
  // call sites can still pass a custom `label`. Crashing on unknown codes is worse
  // than showing them in the neutral grey slot.
  if (props.entity === "timesheet") return TIMESHEET_TO_SLOT[props.code] ?? "draft";
  return ABSENCE_TO_SLOT[props.code] ?? "draft";
}

function resolveLabel(props: StatusBadgeProps): string {
  if (props.label != null) return props.label;
  if (props.entity === "timesheet") return TIMESHEET_LABEL[props.code] ?? props.code;
  return ABSENCE_LABEL[props.code] ?? props.code;
}

const SIZE_PADDING: Record<StatusBadgeSize, string> = {
  xs: "0 6px",
  sm: "0 8px",
  md: "0 10px",
};

const SIZE_HEIGHT: Record<StatusBadgeSize, number> = {
  xs: 18,
  sm: 22,
  md: 26,
};

const SIZE_FONT: Record<StatusBadgeSize, number> = {
  xs: 10.5,
  sm: 11.5,
  md: 12.5,
};

function dotForVariant(slot: StatusSlot, colors: SlotColors): ReactNode {
  return (
    <span
      data-testid={`status-dot-${slot}`}
      style={{
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: colors.fg,
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  );
}

export function StatusBadge(props: StatusBadgeProps) {
  const { size = "sm", variant = "soft", withIcon = false } = props;
  const { colorScheme } = useMantineColorScheme();
  const scheme: Scheme = colorScheme === "dark" ? "dark" : "light";

  const slot = resolveSlot(props);
  const label = resolveLabel(props);
  // Tokens are the source of truth — independent of MantineProvider theme.other
  // so the component renders correctly even when callers don't apply our theme.
  const colors = STATUS[scheme][slot];
  const icon = withIcon ? SLOT_ICON[slot] : undefined;

  const styles: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    height: SIZE_HEIGHT[size],
    padding: SIZE_PADDING[size],
    fontSize: SIZE_FONT[size],
    fontWeight: 600,
    lineHeight: 1,
    borderRadius: 4,
    border: "1px solid transparent",
    boxSizing: "border-box",
    color: colors.fg,
    background:
      variant === "outline" ? "transparent" : variant === "dot" ? "transparent" : colors.bg,
    borderColor:
      variant === "outline" ? colors.border : variant === "dot" ? "transparent" : colors.border,
  };

  return (
    <Badge
      data-testid={`status-badge-${props.entity}-${slot}`}
      data-status-slot={slot}
      data-variant={variant}
      unstyled
      component="span"
      style={styles}
    >
      {variant === "dot" && dotForVariant(slot, colors)}
      {icon && (
        <span aria-hidden="true" style={{ fontSize: SIZE_FONT[size] * 0.95, lineHeight: 1 }}>
          {icon}
        </span>
      )}
      {label}
    </Badge>
  );
}

/** Public for tests / external callers building custom UIs. */
export const STATUS_SLOT_MAPS = {
  timesheet: TIMESHEET_TO_SLOT,
  absence: ABSENCE_TO_SLOT,
} as const;

export const STATUS_LABEL_MAPS = {
  timesheet: TIMESHEET_LABEL,
  absence: ABSENCE_LABEL,
} as const;
